import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

function axiosErrorMessage(err: unknown): string {
  const e = err as {
    message?: string;
    response?: { status?: number; data?: { message?: string; status?: number } };
  };
  const fromBody = e?.response?.data?.message;
  if (fromBody) return String(fromBody);
  if (e?.response?.status === 503) {
    return 'Service unavailable (503). If provisioning, check PLATFORM_TENANT_PROVISIONING_DISABLED or wait for a long pack-install to finish.';
  }
  return e?.message || 'Platform tenancy request failed';
}

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  try {
    const body = (await promise) as ApiBody<T>;
    if (body?.data === undefined) throw new Error(body?.message || 'Platform tenancy request failed');
    return body.data;
  } catch (err) {
    throw new Error(axiosErrorMessage(err));
  }
}

export const platformTenancyService = {
  getRuntime: () => unwrap<Record<string, unknown>>(api.get('/platform/runtime')),
  listTenants: (params?: Record<string, string>) =>
    unwrap<{ tenants: any[]; kpis: Record<string, unknown> }>(
      api.get('/platform/tenants', { params })
    ),
  getTenant: (id: string) => unwrap<Record<string, unknown>>(api.get(`/platform/tenants/${id}`)),
  createTenant: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post('/platform/tenants', payload)),
  provision: (id: string) =>
    unwrap<Record<string, unknown>>(api.post(`/platform/tenants/${id}/provision`, {})),
  retryProvision: (id: string, fromStep?: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`/platform/tenants/${id}/provision/retry`, { fromStep })
    ),
  rollback: (id: string) =>
    unwrap<Record<string, unknown>>(api.post(`/platform/tenants/${id}/provision/rollback`, {})),
  activate: (id: string, reason?: string) =>
    unwrap<Record<string, unknown>>(api.post(`/platform/tenants/${id}/activate`, { reason })),
  suspend: (id: string, reason?: string) =>
    unwrap<Record<string, unknown>>(api.post(`/platform/tenants/${id}/suspend`, { reason })),
  archive: (id: string, reason?: string) =>
    unwrap<Record<string, unknown>>(api.post(`/platform/tenants/${id}/archive`, { reason })),
  getProvisioning: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`/platform/tenants/${id}/provisioning`)),
  getDiagnostics: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`/platform/tenants/${id}/diagnostics`)),
  runDiagnostics: (id: string) =>
    unwrap<Record<string, unknown>>(api.post(`/platform/tenants/${id}/diagnostics/run`, {})),
  getAudit: (id: string) => unwrap<unknown[]>(api.get(`/platform/tenants/${id}/audit`)),
  listAttachableCompanies: (id: string) =>
    unwrap<Array<{ id: string; code: string; name: string }>>(
      api.get(`/platform/tenants/${id}/attachable-companies`)
    ),
  attachCompany: (id: string, company_id: string) =>
    unwrap<Record<string, unknown>>(api.post(`/platform/tenants/${id}/companies`, { company_id })),
};
